import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  getReportTemplates,
  updateReportTemplate,
  deleteReportTemplate,
  getInvestigationTypes,
  getTests,
} from "api/reportTemplate";
import {
  REPORT_KINDS,
  STATUS_OPTIONS,
  reportKindLabel,
} from "config/reportTemplateOptions";
import ReportTemplateFormModal from "./ReportTemplateFormModal";
import ReportTemplateImportModal from "./ReportTemplateImportModal";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import {
  collectAllPages,
  exportReportTemplates,
} from "utils/reportTemplateExport";

const parseList = (res) =>
  res?.data?.data?.data || res?.data?.data || res?.data || [];

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${d.getFullYear()}`;
};

const ReportTemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [investigationTypes, setInvestigationTypes] = useState([]);
  const [tests, setTests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [invTypeFilter, setInvTypeFilter] = useState("");
  const [testFilter, setTestFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selected, setSelected] = useState({});
  const [deleteId, setDeleteId] = useState("");
  const [exportAnchor, setExportAnchor] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const res = await getReportTemplates({ page: 1, perPage: 1000 });
      setTemplates(parseList(res));
    } catch {
      toast.error("Error while fetching report templates!");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [itRes, testRes] = await Promise.all([
        getInvestigationTypes({ activeOnly: true, perPage: 500 }),
        getTests({ perPage: 1000 }),
      ]);
      setInvestigationTypes(parseList(itRes));
      setTests(parseList(testRes));
    } catch {
      setInvestigationTypes([]);
      setTests([]);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchReferenceData();
  }, []);

  const matchesFilters = useCallback(
    (t) => {
      const term = search.trim().toLowerCase();
      if (term) {
        const hay = `${t.name || ""} ${t.code || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (invTypeFilter) {
        const id = t.investigationType?._id || t.investigationType;
        if (id !== invTypeFilter) return false;
      }
      if (testFilter) {
        const id = t.test?._id || t.test;
        if (id !== testFilter) return false;
      }
      if (kindFilter && t.reportKind !== kindFilter) return false;
      if (statusFilter === "active" && t.isActive === false) return false;
      if (statusFilter === "inactive" && t.isActive !== false) return false;
      return true;
    },
    [search, invTypeFilter, testFilter, kindFilter, statusFilter],
  );

  const filtered = useMemo(
    () => templates.filter(matchesFilters),
    [templates, matchesFilters],
  );

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleReset = () => {
    setSearch("");
    setInvTypeFilter("");
    setTestFilter("");
    setKindFilter("");
    setStatusFilter("");
    setPage(0);
  };

  const openCreate = () => {
    setSelected({});
    setFormModal(true);
  };

  const openEdit = (item) => {
    setSelected(item);
    setFormModal(true);
  };

  const openView = (item) => {
    setSelected(item);
    setViewModal(true);
  };

  const handleFormClose = (type) => {
    setFormModal(false);
    setSelected({});
    if (type !== "close") fetchTemplates();
  };

  const handleToggleStatus = async (item) => {
    try {
      await updateReportTemplate(item._id, {
        isActive: item.isActive === false,
      });
      toast.success(
        item.isActive === false ? "Template activated" : "Template deactivated",
      );
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Status update failed!");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteReportTemplate(deleteId);
      toast.success("Report Template deleted successfully");
      setDeleteId("");
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed!");
    }
  };

  const handleExport = async (format) => {
    setExportAnchor(null);
    if (isExporting) return;
    setIsExporting(true);
    try {
      const allTemplates = await collectAllPages(async (page, perPage) => {
        const res = await getReportTemplates({ page, perPage });
        return {
          items: parseList(res),
          totalPages: res?.data?.meta?.totalPages,
          total: res?.data?.meta?.total,
        };
      });
      const rows = allTemplates.filter(matchesFilters);
      if (!rows.length) {
        toast.info("No report templates to export.");
        return;
      }
      exportReportTemplates(rows, format, "global");
      toast.success(`${rows.length} report template(s) exported.`);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    "S.No",
    "Template Name",
    "Code",
    "Investigation Type",
    "Test",
    "Report Kind",
    "Status",
    "Created",
    "Updated",
    "Actions",
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" fontWeight="bold">
          Report Template Master
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            disabled={isLoading || isExporting || filtered.length === 0}
            sx={{ borderColor: "#194AF5", color: "#194AF5" }}
          >
            {isExporting ? "Exporting…" : "Export"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => setImportOpen(true)}
            disabled={isLoading}
            sx={{ borderColor: "#194AF5", color: "#194AF5" }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            onClick={openCreate}
            sx={{ boxShadow: "none" }}
          >
            + Add Template
          </Button>
        </Box>
      </Grid>

      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
      >
        <MenuItem onClick={() => handleExport("json")}>Export as JSON</MenuItem>
        <MenuItem onClick={() => handleExport("csv")}>Export as CSV</MenuItem>
      </Menu>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Search by Name / Code"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Investigation Type</InputLabel>
            <Select
              value={invTypeFilter}
              label="Investigation Type"
              onChange={(e) => {
                setInvTypeFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {investigationTypes.map((it) => (
                <MenuItem key={it._id} value={it._id}>
                  {it.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Test</InputLabel>
            <Select
              value={testFilter}
              label="Test"
              onChange={(e) => {
                setTestFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {tests.map((t) => (
                <MenuItem key={t._id} value={t._id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Report Kind</InputLabel>
            <Select
              value={kindFilter}
              label="Report Kind"
              onChange={(e) => {
                setKindFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {REPORT_KINDS.map((k) => (
                <MenuItem key={k.value} value={k.value}>
                  {k.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={1.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={1}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleReset}
            sx={{ borderColor: "#194AF5", color: "#194AF5", height: "40px" }}
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer
        sx={{ borderRadius: "12px", border: "1px solid #E0E0E0" }}
      >
        <Table>
          <TableHead sx={{ background: "#F0F4FF" }}>
            <TableRow>
              {columns.map((h) => (
                <TableCell
                  key={h}
                  sx={{ fontWeight: "bold", fontSize: "13px" }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ?
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            : paged.length === 0 ?
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 5, color: "text.secondary" }}
                >
                  No Report Template Found!
                </TableCell>
              </TableRow>
            : paged.map((item, idx) => {
                const active = item.isActive !== false;
                return (
                  <TableRow key={item._id} hover>
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#194AF5" }}>
                      {item.code}
                    </TableCell>
                    <TableCell>{item.investigationType?.name || "-"}</TableCell>
                    <TableCell>{item.test?.name || "-"}</TableCell>
                    <TableCell>{reportKindLabel(item.reportKind)}</TableCell>
                    <TableCell>
                      <Chip
                        label={active ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          bgcolor: active ? "#E6F9EE" : "#FFE6E6",
                          color: active ? "green" : "red",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(item.createdAt)}</TableCell>
                    <TableCell>{formatDate(item.updatedAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => openView(item)}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(item)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={active ? "Deactivate" : "Activate"}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(item)}
                          >
                            {active ?
                              <ToggleOnIcon fontSize="small" color="success" />
                            : <ToggleOffIcon
                                fontSize="small"
                                color="disabled"
                              />
                            }
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(item._id)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filtered.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {/* Create / Edit */}
      {formModal && (
        <ReportTemplateFormModal
          open={formModal}
          onClose={handleFormClose}
          data={selected}
          investigationTypes={investigationTypes}
          tests={tests}
        />
      )}

      {/* View (read-only, reuses the form layout) */}
      {viewModal && (
        <ReportTemplateFormModal
          open={viewModal}
          onClose={() => {
            setViewModal(false);
            setSelected({});
          }}
          data={selected}
          readOnly
          investigationTypes={investigationTypes}
          tests={tests}
        />
      )}

      {importOpen && (
        <ReportTemplateImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={fetchTemplates}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        open={Boolean(deleteId)}
        type="reportTemplate"
        close={() => setDeleteId("")}
        confirm={handleDelete}
      />
    </Box>
  );
};

export default ReportTemplateList;
